import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FilmStrip,
  Download,
  Spinner,
  PencilSimple,
  ArrowRight,
  Trash,
  MusicNote,
  Image,
  UploadSimple,
  Play,
  X,
  Microphone,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const downloadReel = async (reelId) => {
  try {
    const response = await axios.get(`${API}/reels/${reelId}/download`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `reel_${reelId}.mp4`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed — try again");
  }
};

const STYLES = [
  { id: "cinematic", label: "Cinematic" },
  { id: "dramatic", label: "Dramatic" },
  { id: "ethereal", label: "Ethereal / Spiritual" },
  { id: "dark_moody", label: "Dark & Moody" },
  { id: "vibrant", label: "Vibrant & Bold" },
  { id: "vintage", label: "Vintage Film" },
];

function ScriptStep({ script, setScript, style, setStyle, sceneCount, setSceneCount, onGenerate, loading }) {
  return (
    <div className="space-y-4" data-testid="reel-script-step">
      <div>
        <label className="block text-sm text-neutral-400 mb-2">Your Script / Message</label>
        <textarea
          data-testid="reel-script-input"
          className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-4 text-white resize-none focus:outline-none focus:border-neutral-500 h-36"
          placeholder="Write your reel script here... e.g. 'God's love is not something you earn — it's something you were born into. Before you knew His name, He already called you His own.'"
          value={script}
          onChange={(e) => setScript(e.target.value)}
        />
      </div>
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-sm text-neutral-400 mb-2">Visual Style</label>
          <select
            data-testid="reel-style-select"
            className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-3 text-white focus:outline-none"
            value={style}
            onChange={(e) => setStyle(e.target.value)}
          >
            {STYLES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="w-32">
          <label className="block text-sm text-neutral-400 mb-2">Scenes</label>
          <select
            data-testid="reel-scene-count"
            className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-3 text-white focus:outline-none"
            value={sceneCount}
            onChange={(e) => setSceneCount(Number(e.target.value))}
          >
            {[3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>{n} scenes</option>
            ))}
          </select>
        </div>
      </div>
      <button
        data-testid="reel-generate-scenes-btn"
        onClick={onGenerate}
        disabled={!script.trim() || loading}
        className="w-full bg-white text-black font-semibold py-3 rounded-lg hover:bg-neutral-200 transition disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {loading ? <Spinner size={20} className="animate-spin" /> : <PencilSimple size={20} />}
        {loading ? "AI is breaking down your script..." : "Generate Scenes"}
      </button>
    </div>
  );
}

function SceneCard({ scene, idx, scenes, setScenes }) {
  const fileRef = useRef(null);

  const updateScene = (field, value) => {
    setScenes((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/reels/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateScene("uploaded_image_path", res.data.path);
      updateScene("uploaded_image_name", file.name);
      toast.success(`Image uploaded for scene ${scene.scene_number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider">
          Scene {scene.scene_number}
        </span>
        <div className="flex items-center gap-2">
          <select
            className="bg-neutral-800 border border-neutral-600 rounded px-2 py-1 text-xs text-white"
            value={scene.duration}
            onChange={(e) => updateScene("duration", Number(e.target.value))}
          >
            {[3, 4, 5, 6].map((d) => (
              <option key={d} value={d}>{d}s</option>
            ))}
          </select>
          {scenes.length > 2 && (
            <button
              onClick={() => setScenes((prev) => prev.filter((_, i) => i !== idx))}
              className="text-red-500 hover:text-red-400"
            >
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>
      <input
        className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-sm text-white focus:outline-none"
        value={scene.narration}
        onChange={(e) => updateScene("narration", e.target.value)}
        placeholder="On-screen text"
      />
      <textarea
        className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-xs text-neutral-300 resize-none h-16 focus:outline-none"
        value={scene.image_prompt}
        onChange={(e) => updateScene("image_prompt", e.target.value)}
        placeholder="Image prompt (AI generates image from this)"
      />
      {/* Image upload option */}
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
        <button
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1 text-xs text-neutral-400 hover:text-white border border-neutral-700 px-2 py-1 rounded transition"
          data-testid={`scene-upload-btn-${idx}`}
        >
          <UploadSimple size={12} />
          {scene.uploaded_image_name ? "Replace Image" : "Upload Own Image"}
        </button>
        {scene.uploaded_image_name && (
          <div className="flex items-center gap-1">
            <Image size={12} className="text-green-400" />
            <span className="text-xs text-green-400 truncate max-w-[120px]">{scene.uploaded_image_name}</span>
            <button
              onClick={() => {
                updateScene("uploaded_image_path", null);
                updateScene("uploaded_image_name", null);
              }}
              className="text-red-400 hover:text-red-300"
            >
              <X size={10} />
            </button>
          </div>
        )}
        {!scene.uploaded_image_name && (
          <span className="text-xs text-neutral-600">or AI will generate from prompt</span>
        )}
      </div>
    </div>
  );
}

function ScenesStep({ scenes, setScenes, musicTrack, setMusicTrack, musicTracks, voiceover, setVoiceover, voice, setVoice, voices, onBack, onGenerate, loading }) {
  const totalDuration = scenes.reduce((sum, s) => sum + (s.duration || 5), 0);

  return (
    <div className="space-y-4" data-testid="reel-scenes-step">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-neutral-400 hover:text-white transition">
          &larr; Back to script
        </button>
        <span className="text-sm text-neutral-500">Total: {totalDuration}s</span>
      </div>
      <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
        {scenes.map((scene, idx) => (
          <SceneCard key={scene.scene_number} scene={scene} idx={idx} scenes={scenes} setScenes={setScenes} />
        ))}
      </div>

      {/* Voiceover */}
      <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="flex items-center gap-2 text-sm text-neutral-400">
            <Microphone size={16} /> AI Voiceover Narration
          </label>
          <button
            onClick={() => setVoiceover(!voiceover)}
            className={`w-10 h-5 rounded-full transition-colors relative ${voiceover ? "bg-white" : "bg-neutral-700"}`}
            data-testid="reel-voiceover-toggle"
          >
            <div className={`w-4 h-4 rounded-full absolute top-0.5 transition-transform ${voiceover ? "bg-black translate-x-5" : "bg-neutral-400 translate-x-0.5"}`} />
          </button>
        </div>
        {voiceover && (
          <select
            data-testid="reel-voice-select"
            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg p-3 text-white focus:outline-none text-sm"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
          >
            {voices.map((v) => (
              <option key={v.id} value={v.id}>{v.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* Music selection */}
      <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
        <label className="flex items-center gap-2 text-sm text-neutral-400 mb-2">
          <MusicNote size={16} /> Background Music
        </label>
        <select
          data-testid="reel-music-select"
          className="w-full bg-neutral-800 border border-neutral-600 rounded-lg p-3 text-white focus:outline-none"
          value={musicTrack}
          onChange={(e) => setMusicTrack(e.target.value)}
        >
          {musicTracks.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>

      <button
        data-testid="reel-create-btn"
        onClick={onGenerate}
        disabled={loading}
        className="w-full bg-white text-black font-semibold py-3 rounded-lg hover:bg-neutral-200 transition disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {loading ? <Spinner size={20} className="animate-spin" /> : <FilmStrip size={20} />}
        {loading ? "Creating your reel..." : "Create Reel"}
      </button>
    </div>
  );
}

function ProgressStep({ job }) {
  if (!job) return null;
  const isProcessing = job.status === "processing";
  const isCompleted = job.status === "completed";
  const isFailed = job.status === "failed";

  return (
    <div className="space-y-4 text-center" data-testid="reel-progress-step">
      {isProcessing && (
        <>
          <Spinner size={40} className="animate-spin mx-auto text-white" />
          <p className="text-neutral-300">{job.current_step || "Working..."}</p>
          <div className="w-full bg-neutral-800 rounded-full h-2">
            <div className="bg-white h-2 rounded-full transition-all animate-pulse" style={{ width: "60%" }} />
          </div>
          <p className="text-xs text-neutral-500">This takes a few minutes — AI images are being generated</p>
        </>
      )}
      {isCompleted && (
        <div className="space-y-4">
          <div className="text-green-400 text-lg font-semibold">Reel Ready!</div>
          {/* Video Preview */}
          <div className="bg-neutral-900 rounded-lg overflow-hidden border border-neutral-700 max-w-[280px] mx-auto">
            <video
              data-testid="reel-preview-video"
              controls
              className="w-full"
              src={`${API}/reels/${job.id}/preview`}
              style={{ maxHeight: "500px" }}
            >
              Your browser does not support video.
            </video>
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => downloadReel(job.id)}
              data-testid="reel-download-btn"
              className="inline-flex items-center gap-2 bg-white text-black font-semibold px-6 py-3 rounded-lg hover:bg-neutral-200 transition"
            >
              <Download size={20} /> Download
            </button>
          </div>
          {/* Buffer scheduling is in standalone section below */}
        </div>
      )}
      {isFailed && (
        <div className="text-red-400">Reel generation failed. Please try again.</div>
      )}
    </div>
  );
}

function BufferSection({ reels }) {
  const [connected, setConnected] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [caption, setCaption] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [token, setToken] = useState("");
  const [showConnect, setShowConnect] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedReel, setSelectedReel] = useState("");

  useEffect(() => {
    axios.get(`${API}/buffer/status`).then((r) => {
      setConnected(r.data.connected);
      setChannels(r.data.channels || []);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reels.length && !selectedReel) setSelectedReel(reels[0]?.id || "");
  }, [reels, selectedReel]);

  const handleConnect = async () => {
    if (!token.trim()) return;
    try {
      const r = await axios.post(`${API}/buffer/connect`, { access_token: token });
      setConnected(true);
      setChannels(r.data.channels || []);
      setShowConnect(false);
      toast.success("Buffer connected!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect to Buffer");
    }
  };

  const handleDisconnect = async () => {
    await axios.post(`${API}/buffer/disconnect`);
    setConnected(false);
    setChannels([]);
    toast.success("Buffer disconnected");
  };

  const handleSchedule = async () => {
    if (!selectedChannels.length || !selectedReel) {
      toast.error("Select a reel and at least one channel");
      return;
    }
    setScheduling(true);
    try {
      const r = await axios.post(`${API}/buffer/schedule`, {
        reel_id: selectedReel,
        text: caption,
        channel_ids: selectedChannels,
        scheduled_time: scheduleTime || null,
      });
      setResult(r.data);
      toast.success(`Scheduled to ${r.data.successful} channel(s)!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scheduling failed");
    } finally {
      setScheduling(false);
    }
  };

  const toggleChannel = (id) => {
    setSelectedChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  return (
    <div className="mt-6 mb-8 bg-neutral-900 border border-neutral-700 rounded-lg p-4" data-testid="buffer-section">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
        <ArrowRight size={14} /> Post to Social Media (Buffer)
      </h3>

      {!connected && !showConnect && (
        <div className="space-y-2">
          <p className="text-xs text-neutral-500">Connect your Buffer account to schedule reels to Facebook, Instagram, and TikTok.</p>
          <button
            onClick={() => setShowConnect(true)}
            className="text-sm bg-white text-black font-semibold px-4 py-2 rounded-lg hover:bg-neutral-200 transition"
            data-testid="buffer-connect-btn"
          >
            Connect Buffer Account
          </button>
        </div>
      )}

      {showConnect && !connected && (
        <div className="space-y-3">
          <p className="text-xs text-neutral-400">
            Paste your Buffer API key. Get it from{" "}
            <a href="https://buffer.com/developer-api" target="_blank" rel="noreferrer" className="text-white underline">
              buffer.com/developer-api
            </a>{" "}
            (Settings → API Keys)
          </p>
          <input
            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-3 text-sm text-white focus:outline-none"
            placeholder="Paste your Buffer access token here"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            type="password"
            data-testid="buffer-token-input"
          />
          <div className="flex gap-2">
            <button
              onClick={handleConnect}
              disabled={!token.trim()}
              className="bg-white text-black text-sm font-semibold px-5 py-2 rounded-lg hover:bg-neutral-200 disabled:opacity-40 transition"
              data-testid="buffer-save-token-btn"
            >
              Connect
            </button>
            <button onClick={() => setShowConnect(false)} className="text-sm text-neutral-500 hover:text-white px-3">
              Cancel
            </button>
          </div>
        </div>
      )}

      {connected && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-green-400 font-medium">Connected to Buffer</span>
            <button onClick={handleDisconnect} className="text-xs text-red-400 hover:text-red-300">
              Disconnect
            </button>
          </div>

          {/* Select a reel */}
          {reels.length > 0 && (
            <div>
              <label className="text-xs text-neutral-400 mb-1 block">Select Reel to Post</label>
              <select
                className="w-full bg-neutral-800 border border-neutral-600 rounded-lg p-3 text-sm text-white focus:outline-none"
                value={selectedReel}
                onChange={(e) => setSelectedReel(e.target.value)}
                data-testid="buffer-reel-select"
              >
                {reels.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.scenes?.[0]?.narration?.slice(0, 50) || "Reel"} — {new Date(r.created_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Channel selection */}
          <div>
            <label className="text-xs text-neutral-400 mb-1 block">Select Channels (click to select)</label>
            <div className="flex flex-wrap gap-2">
              {channels.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => toggleChannel(ch.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    selectedChannels.includes(ch.id)
                      ? "bg-white text-black border-white"
                      : "border-neutral-600 text-neutral-400 hover:text-white"
                  }`}
                >
                  {ch.service}: {ch.name}
                </button>
              ))}
              {channels.length === 0 && (
                <span className="text-xs text-neutral-500">No connected channels — add channels in your Buffer dashboard first</span>
              )}
            </div>
          </div>

          {/* Caption */}
          <textarea
            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-3 text-sm text-white resize-none h-20 focus:outline-none"
            placeholder="Write a caption for your post..."
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            data-testid="buffer-caption-input"
          />

          {/* Schedule time */}
          <div>
            <label className="text-xs text-neutral-400 mb-1 block">Schedule (leave blank to add to queue)</label>
            <input
              type="datetime-local"
              className="bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              data-testid="buffer-schedule-time"
            />
          </div>

          <button
            onClick={handleSchedule}
            disabled={scheduling || !selectedChannels.length || !selectedReel}
            className="w-full bg-white text-black font-semibold py-3 rounded-lg hover:bg-neutral-200 transition disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="buffer-schedule-btn"
          >
            {scheduling ? <Spinner size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {scheduling ? "Scheduling..." : "Schedule Post"}
          </button>

          {result && (
            <div className="text-xs mt-1">
              <span className="text-green-400">{result.successful} scheduled</span>
              {result.errors?.length > 0 && (
                <span className="text-red-400 ml-2">{result.errors.length} failed</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReelHistory({ reels }) {
  const [previewId, setPreviewId] = useState(null);

  if (!reels.length) return null;
  return (
    <div className="mt-6 space-y-2" data-testid="reel-history">
      <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider">Previous Reels</h3>
      {reels.map((reel) => (
        <div key={reel.id}>
          <div className="flex items-center justify-between bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="flex items-center gap-3">
              <FilmStrip size={18} className="text-neutral-500" />
              <div>
                <div className="text-sm text-white">
                  {reel.scenes?.[0]?.narration?.slice(0, 40) || "Reel"}...
                </div>
                <div className="text-xs text-neutral-500">
                  {new Date(reel.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPreviewId(previewId === reel.id ? null : reel.id)}
                className="text-neutral-400 hover:text-white transition"
                data-testid={`reel-preview-btn-${reel.id}`}
              >
                <Play size={18} />
              </button>
              <button
                onClick={() => downloadReel(reel.id)}
                className="text-neutral-400 hover:text-white transition"
              >
                <Download size={18} />
              </button>
            </div>
          </div>
          {previewId === reel.id && (
            <div className="mt-2 bg-neutral-900 rounded-lg overflow-hidden border border-neutral-700 max-w-[280px] mx-auto">
              <video controls className="w-full" src={`${API}/reels/${reel.id}/preview`} style={{ maxHeight: "400px" }}>
                Your browser does not support video.
              </video>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function ReelStudio() {
  const [step, setStep] = useState("script");
  const [script, setScript] = useState("");
  const [style, setStyle] = useState("cinematic");
  const [sceneCount, setSceneCount] = useState(5);
  const [scenes, setScenes] = useState([]);
  const [musicTrack, setMusicTrack] = useState("none");
  const [musicTracks, setMusicTracks] = useState([{ id: "none", label: "No Music" }]);
  const [voiceover, setVoiceover] = useState(false);
  const [voice, setVoice] = useState("onyx");
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [reels, setReels] = useState([]);

  // Load music tracks, voices, and past reels
  useEffect(() => {
    axios.get(`${API}/reels/music-tracks`).then((r) => setMusicTracks(r.data)).catch(() => {});
    axios.get(`${API}/reels/voices`).then((r) => setVoices(r.data)).catch(() => {});
    axios.get(`${API}/reels`).then((r) => setReels(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll job status
  useEffect(() => {
    if (!currentJobId) return;
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/reels/job/${currentJobId}`);
        setJob(r.data);
        if (r.data.status === "completed" || r.data.status === "failed") {
          clearInterval(interval);
          setLoading(false);
          if (r.data.status === "completed") {
            toast.success("Your reel is ready!");
            axios.get(`${API}/reels`).then((r2) => setReels(r2.data)).catch(() => {});
          }
        }
      } catch {
        clearInterval(interval);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [currentJobId]);

  const handleGenerateScenes = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reels/scenes`, { script, style, scene_count: sceneCount });
      setScenes(res.data.scenes);
      setStep("scenes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate scenes");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReel = async () => {
    setLoading(true);
    setStep("progress");
    try {
      const res = await axios.post(`${API}/reels/generate`, { scenes, music_track: musicTrack, voiceover, voice });
      setCurrentJobId(res.data.reel_id);
      setJob({ id: res.data.reel_id, status: "processing", current_step: "Starting..." });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start reel generation");
      setLoading(false);
      setStep("scenes");
    }
  };

  const resetAll = () => {
    setStep("script");
    setScenes([]);
    setCurrentJobId(null);
    setJob(null);
    setScript("");
    setMusicTrack("none");
    setVoiceover(false);
    setVoice("onyx");
  };

  return (
    <div className="h-full overflow-y-auto p-6" data-testid="reel-studio">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FilmStrip size={24} /> Reel Studio
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              Write a script &rarr; AI generates scenes &rarr; Preview &amp; download your reel
            </p>
          </div>
          {step !== "script" && (
            <button
              onClick={resetAll}
              className="text-sm text-neutral-400 hover:text-white border border-neutral-700 px-3 py-1 rounded-lg transition"
              data-testid="reel-new-btn"
            >
              + New Reel
            </button>
          )}
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {["Script", "Scenes", "Preview"].map((label, i) => {
            const stepNames = ["script", "scenes", "progress"];
            const active = stepNames.indexOf(step) >= i;
            return (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-white text-black" : "bg-neutral-800 text-neutral-500"}`}>
                  {i + 1}
                </div>
                <span className={`text-xs ${active ? "text-white" : "text-neutral-600"}`}>{label}</span>
                {i < 2 && <ArrowRight size={12} className="text-neutral-600 mx-1" />}
              </div>
            );
          })}
        </div>

        {step === "script" && (
          <ScriptStep
            script={script} setScript={setScript}
            style={style} setStyle={setStyle}
            sceneCount={sceneCount} setSceneCount={setSceneCount}
            onGenerate={handleGenerateScenes} loading={loading}
          />
        )}
        {step === "scenes" && (
          <ScenesStep
            scenes={scenes} setScenes={setScenes}
            musicTrack={musicTrack} setMusicTrack={setMusicTrack}
            musicTracks={musicTracks}
            voiceover={voiceover} setVoiceover={setVoiceover}
            voice={voice} setVoice={setVoice} voices={voices}
            onBack={() => setStep("script")}
            onGenerate={handleCreateReel} loading={loading}
          />
        )}
        {step === "progress" && <ProgressStep job={job} />}

        <ReelHistory reels={reels} />

        {/* Buffer - always visible */}
        <BufferSection reels={reels} />
      </div>
    </div>
  );
}
